<template>
  <div style="margin-top: 120px;" :class="['catalouge-view', themeClass]">
    <div class="titlebar"> 

      <button class="theme-toggle"
              type="button"
              :aria-label="isLight ? $t('katalog.switch_to_dark') : $t('katalog.switch_to_light')"
              @click="toggleTheme">
        <span v-if="isLight">🌙</span>
        <span v-else>☀️</span>
      </button>
    </div>

    <div v-for="group in groupedCategories" :key="group.key" class="category-block">
      <div class="category-header">
        <img :src="require(`./icons/noah-logo.svg`)" class="account-icon" width="150" />
        <p>{{ group.label }}</p>
      </div>

      <div class="items">
        <div class="item-card" v-for="it in group.items" :key="it.item_id">
          <!-- Sol panel: metin alanları -->
          <div class="col info">
            <div v-if="it.title" class="it-title">{{ it.title }}</div>
            <div class="it-name">{{ it.name }}</div>
            <div v-if="it.seri_no" class="it-seri">{{ $t('katalog.seri_no') }} {{ it.seri_no }}</div>

            <div class="chips">
              <!-- ALT TONE CHIP (ikon + dinamik renk) -->
              <span
                v-if="it.alt_tone"
                class="chip alt"
                :class="toneClass(it.alt_tone)"
              >
                <img
                  v-if="toneIcon(it.alt_tone)"
                  :src="toneIcon(it.alt_tone)"
                  alt=""
                  class="chip-icon"
                />
                {{ it.alt_tone }}
              </span>

              <!-- Pigment tipi -->
              <span v-if="it.pigment_type" class="chip pigment">{{ it.pigment_type }}</span>
            </div>

            <div v-if="it.description" class="it-desc">
              {{ it.description }}
            </div>
          </div>

          <!-- Orta panel: sample_img -->
          <div class="col img sample">
            <img v-if="it.sample_img" :src="it.sample_img" alt="sample_img" />
            <div v-else class="placeholder">sample_img</div>
          </div>

          <!-- Sağ panel: color_img -->
          <div class="col img color">
            <img v-if="it.color_img" :src="it.color_img" alt="color_img" />
            <div v-else class="placeholder">color_img</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="state">{{ $t('common.loading') }}</div>
    <div v-else-if="error" class="state error">{{ error }}</div>
  </div>
</template>

<script>
import axios from "axios";

// İkonları statik import ederek paketleyici uyumluluğunu garanti ediyoruz
import SOGUK_ICON from "./icons/soguk.svg";
import SICAK_ICON from "./icons/sicak.svg";
import NOTR_ICON from "./icons/notr.svg";

export default {
  name: "CatalougeView",
  data() {
    return {
      items: [],
      loading: false,
      error: null,
      theme: "light", // 'light' | 'dark'
      toneIconMap: {
        soguk: SOGUK_ICON,
        sicak: SICAK_ICON,
        notr: NOTR_ICON,
      },
    };
  },
  computed: {
    isLight() { return this.theme === "light"; },
    themeClass() { return this.isLight ? "theme--light" : "theme--dark"; },
    groupedCategories() {
      const map = new Map();
      for (const it of this.items) {
        const label =
          it.category_name || it.category || it.cat_name || this.$t('katalog.category_fallback', { id: it.cat_id ?? "-" });
        if (!map.has(label)) map.set(label, []);
        map.get(label).push(it);
      }
      const byOrder = (a, b) => {
        const ax = Number.isFinite(+a.order_no) ? +a.order_no : Infinity;
        const bx = Number.isFinite(+b.order_no) ? +b.order_no : Infinity;
        if (ax !== bx) return ax - bx;
        return String(a.name || "").localeCompare(String(b.name || ""));
      };
      return Array.from(map.entries())
        .map(([label, items]) => ({ key: label, label, items: items.slice().sort(byOrder) }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label)));
    },
  },
  methods: {
    async fetchItems() {
      this.loading = true;
      this.error = null;
      try {
        const res = await axios.get("/auth/promo/catalouge/items");
        this.items =
          res.data?.data || res.data?.items || (Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        this.error = this.$t('katalog.fetch_error');
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    /** "Soğuk"/"Sıcak"/"Nötr" -> "soguk"/"sicak"/"notr" */
    normalizeTone(val) {
      if (!val) return "";
      const s = String(val).trim().toLowerCase();
      return s
        .replace(/ğ/g, "g")
        .replace(/ü/g, "u")
        .replace(/ş/g, "s")
        .replace(/ı/g, "i")
        .replace(/ö/g, "o")
        .replace(/ç/g, "c")
        .replace(/â|î|û/g, (m) => ({ "â": "a", "î": "i", "û": "u" }[m] || m))
        .replace(/[^a-z0-9]/g, "");
    },
    toneIcon(val) {
      const key = this.normalizeTone(val);
      return this.toneIconMap[key] || null;
    },
    toneClass(val) {
      const key = this.normalizeTone(val);
      if (key === "soguk") return "alt--soguk";
      if (key === "sicak") return "alt--sicak";
      if (key === "notr") return "alt--notr";
      return "";
    },
    toggleTheme() {
      this.theme = this.isLight ? "dark" : "light";
      try { localStorage.setItem("noahTheme", this.theme); } catch (err) { void err; }
    },
    initTheme() {
      try {
        const saved = localStorage.getItem("noahTheme");
        if (saved === "light" || saved === "dark") this.theme = saved;
        else if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
          this.theme = "dark";
        }
      } catch (err) { void err; }
    },
  },
  mounted() {
    this.initTheme();
    this.fetchItems();
  },
};
</script>

<style scoped>
/* =========================================================
   TEMEL YAPI: Tüm renkler CSS değişkenlerinden gelir.
   .theme--light ve .theme--dark bu değişkenleri farklı set eder.
   ========================================================= */

/* LIGHT THEME TOKENS */
.catalouge-view.theme--light {
  --bg: #f7f8fa;
  --text: #111827;
  --muted: #374151;
  --title: #1f2937;

  --card-bg: #ffffff;
  --card-border: #e5e7eb;
  --card-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);

  --chip-text: #0f172a;
  --chip-border: rgba(0,0,0,0.06);
  --pigment-bg: #f3f4f6;
  --pigment-text: #111827;

  --desc-bg: #f9fafb;
  --desc-text: #374151;
  --desc-border: #eef2f7;

  --img-bg: #f8fafc;
  --img-border: #e5e7eb;

  --ph-text: #6b7280;
  --ph-bg: #f3f4f6;
  --ph-border: #d1d5db;

  --state-text: #374151;
  --state-error: #b91c1c;

  --cat-grad-1: #ffffff;
  --cat-grad-2: #f1f5f9;
  --cat-border: #e5e7eb;
  --cat-text: #374151;

  /* alt tone chip özel arkaplan/border */
  --alt-soguk-bg: #e6f0ff;
  --alt-soguk-border: #cfe1ff;
  --alt-sicak-bg: #ffeadd;
  --alt-sicak-border: #ffd4bb;
  --alt-notr-bg: #f3f4f6;
  --alt-notr-border: #e5e7eb;

  --toggle-bg: #ffffff;
  --toggle-border: #e5e7eb;
  --toggle-text: #111827;
  --toggle-hover: #f3f4f6;
}

/* DARK THEME TOKENS (orijinal koyu stilin karşılığı) */
.catalouge-view.theme--dark {
  --bg: #0e0f0f;
  --text: #e5e7eb;
  --muted: #c4c4c4;
  --title: #cfd3d8;

  --card-bg: #1f1f1f;
  --card-border: transparent;
  --card-shadow: none;

  --chip-text: #ffffff;
  --chip-border: rgba(255,255,255,0.08);
  --pigment-bg: #2b2b2b;
  --pigment-text: #ffffff;

  --desc-bg: #1a1a1a;
  --desc-text: #cfd3d8;
  --desc-border: #232323;

  --img-bg: #111111;
  --img-border: #1a1a1a;

  --ph-text: #eaeaea;
  --ph-bg: #000000;
  --ph-border: #2a2a2a;

  --state-text: #cfd3d8;
  --state-error: #ff6b6b;

  --cat-grad-1: #1f1f1f;
  --cat-grad-2: #0a0a0a;
  --cat-border: transparent;
  --cat-text: #c4c4c4;

  /* alt tone chip özel arkaplan/border (koyu) */
  --alt-soguk-bg: #4e6d8b;
  --alt-soguk-border: #4e6d8b;
  --alt-sicak-bg: #704b39;
  --alt-sicak-border: #704b39;
  --alt-notr-bg: #302f2f;
  --alt-notr-border: #302f2f;

  --toggle-bg: #232323;
  --toggle-border: #2e2e2e;
  --toggle-text: #e5e7eb;
  --toggle-hover: #2b2b2b;
}

/* =========================================================
   GENEL STİLLER (değişkenleri kullanır)
   ========================================================= */
.catalouge-view {
  background: var(--bg);
  padding: 16px;
  color: var(--text);
}

/* Başlık + tema butonu */
.titlebar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center; /* başlığı ortada tut */
  margin-bottom: 1rem;
}
.title {
  text-align: center;
  margin: 2rem 0;
  font-size: 1.4rem;
  font-weight: 300;
  color: var(--title);
}
.theme-toggle {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(calc(-50% + 4px));
  border: 1px solid var(--toggle-border);
  background: var(--toggle-bg);
  color: var(--toggle-text);
  padding: 8px 10px;
  border-radius: 999px;
  line-height: 1;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background .15s ease, border-color .15s ease, transform .05s ease;
}
.theme-toggle:hover { background: var(--toggle-hover); }
.theme-toggle:active { transform: translateY(calc(-50% + 4px)) scale(0.98); }

/* Kategori başlığı */
.category-header {
  display: flex;
  padding: 18px 10px;
  margin: 1rem 0;
  border-radius: 8px;
  font-size: 1.2rem;
  font-weight: 400;
  color: var(--cat-text);
  background: linear-gradient(45deg, var(--cat-grad-1), var(--cat-grad-2));
  border: 1px solid var(--cat-border);
}

/* Grid alanı */
.items {
  display: grid;
  gap: 14px;
}

/* Ürün kartı */
.item-card {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr;
  gap: 14px;
  background: var(--card-bg);
  border-radius: 10px;
  padding: 14px;
  border: 1px solid var(--card-border);
  box-shadow: var(--card-shadow);
}

/* Metinler */
.it-title { color: var(--muted); font-size: 16px; margin-bottom: 2px; }
.it-name  { font-weight: 800; font-size: 20px; margin-bottom: 4px; color: var(--text); }
.it-seri  { color: var(--muted); opacity: 0.95; margin-bottom: 10px; }

/* Chip’ler */
.chips { display: flex; gap: 10px; margin-bottom: 10px; }
.chip {
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 13px;
  letter-spacing: .02em;
  border: 1px solid var(--chip-border);
}
.chip.alt { color: var(--chip-text); }
.chip.pigment { background: var(--pigment-bg); color: var(--pigment-text); }

/* Alt tone ikonları */
.chip-icon { width: 16px; height: 16px; display: inline-block; }

/* Alt ton – dinamik arkaplanlar */
.chip.alt.alt--soguk { background: var(--alt-soguk-bg); border-color: var(--alt-soguk-border); }
.chip.alt.alt--sicak { background: var(--alt-sicak-bg); border-color: var(--alt-sicak-border); }
.chip.alt.alt--notr  { background: var(--alt-notr-bg);  border-color: var(--alt-notr-border); }

/* Açıklama kutusu */
.it-desc {
  padding: 14px 12px;
  border-radius: 8px;
  line-height: 1.5;
  background: var(--desc-bg);
  color: var(--desc-text);
  border: 1px solid var(--desc-border);
}

/* Görsel alanları */
.col.img {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--img-bg);
  border-radius: 10px;
  min-height: 120px;
  max-height: 240px;
  border: 1px solid var(--img-border);
}
.col.img img {
  width: 100%;
  height: 100%;
  max-height: 320px;
  object-fit: cover;
  border-radius: 10px;
}

/* Placeholder’lar */
.placeholder {
  width: 100%;
  height: 100%;
  min-height: 230px;
  display: grid;
  place-items: center;
  color: var(--ph-text);
  background: var(--ph-bg);
  border-radius: 10px;
  font-size: 15px;
  border: 1px dashed var(--ph-border);
}

/* Durum mesajları */
.state { margin-top: 16px; color: var(--state-text); }
.state.error { color: var(--state-error); }

/* Responsive */
@media (max-width: 1024px) {
  .item-card { grid-template-columns: 1fr 1fr; }
  .col.color { order: 3; grid-column: 1 / -1; }
}
@media (max-width: 720px) {
  .item-card { grid-template-columns: 1fr; }
  .col.img, .placeholder { min-height: 200px; }
  .theme-toggle { right: 8px; }
}
</style>
